import { GameType } from './index.model';
import { connectToFights, IWS, connectToMain, sendMainRequest } from './websocket-service';
import store from '@/store';
import { getFightResultFromBinary, keysToCamel } from './helpers';
import { debounce } from 'lodash';
import { getCockfightPaneNamespaces } from '@/store/cockfight-pane';

let mgConnection: IWS | null = null;
let taiXiuConnection: IWS | null = null;
let mainConnection: IWS | null = null;
const arenasStatus: Record<string, string> = {};

/*
 * Both arena lists, not just the cockfight one: Tai Xiu tables come from their
 * own call, so a table the backend turned on or off was only picked up on a
 * manual reload. Debounced because these pushes arrive in bursts and each one
 * costs two requests.
 */
const refreshArenaLists = debounce(() => {
  store.dispatch('general/getArenas');
  store.dispatch('taiXiu/getArenas');
}, 1000);

/*
 * A fight on an arena neither list has: the back office opened betting on an
 * arena (or Tài Xỉu table) the client loaded before it existed, and every
 * handler drops fights it cannot place -- so its chip never appeared. Reload
 * both lists. At most once per arena per cooldown, in case the endpoint does
 * not list it yet: otherwise every tick on that arena would refetch.
 */
const UNKNOWN_ARENA_RETRY_MS = 30 * 1000;
const unknownArenaRefreshedAt: Record<string, number> = {};

const isKnownArena = (arenaId: string): boolean =>
  (store.getters['general/arenas'] || []).some((item: any) => item.id === arenaId) ||
  (store.getters['taiXiu/tables'] || []).some((item: any) => item.arenaId === arenaId || item.id === arenaId);

const refreshIfUnknownArena = (data: any) => {
  const arenaId = data?.match_day?.arena?.id;
  if (!arenaId || isKnownArena(arenaId)) return;
  const now = Date.now();
  if (now - (unknownArenaRefreshedAt[arenaId] || 0) < UNKNOWN_ARENA_RETRY_MS) return;
  unknownArenaRefreshedAt[arenaId] = now;
  refreshArenaLists();
};

const initMgConnection = (token: string, oddsType: string = 'eu') => {
  disconnectMgConnection();
  mgConnection = wsFights(token, oddsType);
};

const initMainConnection = (token: string) => {
  disconnectMainConnection();
  store.commit('general/RESET_PENDING_ARENA_STREAMS');
  mainConnection = connectToMain(
    (res) => {
      const { type, data } = res;
      if (type === 'fight_result') {
        const fightResults = {
          fightsTotal: {
            bankerCount: data.bankerCount,
            drawCount: data.drawCount,
            playerCount: data.playerCount,
          },
          fights: getFightResultFromBinary(data.fights),
        };
        store.commit('fight/INSERT_CHART_FIGHTS', fightResults.fights);
        store.commit('fight/INSERT_CHART_FIGHTS_TOTAL', fightResults.fightsTotal);
      } else if (type === 'arena_stream') {
        // Unsolicited push on the main socket: carries only a url, and does not
        // correspond to a pending request, so it stays on the global-only
        // mutation rather than consuming a per-arena correlation slot.
        store.commit('general/UPDATE_ARENA_STREAM', data.url);
      } else if (type === 'refresh_stream_url') {
        store.dispatch('general/getArenas');
      }
    },
    () => {
      initMainConnection(token);
    },
    token,
    GameType.main
  );
};
/*
 * Reconnect for players resuming from the background. Every player on screen
 * runs this on the same visibility event; each reconnect drops the requests the
 * previous one's player had just sent, so only the first in a burst may do it.
 */
const RECONNECT_MAIN_MIN_INTERVAL = 3000;
let lastMainReconnectAt = 0;
const reconnectMainConnection = (token: string) => {
  const now = Date.now();
  if (now - lastMainReconnectAt < RECONNECT_MAIN_MIN_INTERVAL) return;
  lastMainReconnectAt = now;
  initMainConnection(token);
};

const updateUserBalance = debounce((balance: number) => {
  store.dispatch('general/updateWallet', Number(balance));
}, 500);

/*
 * Whether a fight update belongs to the fight a view is showing, or replaces it.
 *
 * A different fight on the same arena used to be taken only off its
 * `confirmed` message. Missing that one message -- loading mid-fight, a socket
 * reconnecting, a background tab -- left the view on the old fight for good,
 * since every later message for the new one was dropped on its id. A higher
 * fight number is a newer fight whatever its status, so it is taken too.
 */
const isFightForArena = (
  data: any,
  incomingArenaId: string | undefined,
  fightId: string,
  arenaId: string,
  fightNumber?: number | string
) => {
  if (!incomingArenaId) return !!data.id && data.id === fightId;
  if (data.id === fightId) return true;
  if (incomingArenaId !== arenaId) return false;
  return data.status === 'confirmed' || Number(data.fight_number) > Number(fightNumber || 0);
};

/*
 * The latest odds push per fight. A `fight_odds` message names its fight but
 * not its arena, so a view only takes it once it knows that fight's id. Right
 * after a reload the view is still waiting for `getCurrentFight`, the push is
 * dropped, and with no new bet nothing follows it -- the bet zone sat on 0x.
 * Kept here, it is applied when a view picks that fight up (see below).
 */
const MAX_BUFFERED_ODDS = 20;
const latestOddsByFightId = new Map<string, any>();

const bufferFightOdds = (data: any) => {
  if (!data || !data.id) return;
  latestOddsByFightId.delete(data.id);
  latestOddsByFightId.set(data.id, keysToCamel(data));
  if (latestOddsByFightId.size > MAX_BUFFERED_ODDS) {
    latestOddsByFightId.delete(latestOddsByFightId.keys().next().value);
  }
};

// Total staked on a fight: pool totals only grow while betting runs, so the
// larger one is the newer of two snapshots.
const totalStaked = (fight: any): number =>
  ((fight && fight.selections) || []).reduce(
    (sum: number, selection: any) => sum + (Number(selection && selection.totalBetAmount) || 0),
    0
  );

const fightIdsByView: { [module: string]: string } = {};
let oddsSubscribed = false;

// Subscribed on the first socket message, not at import: `store` imports this
// file (via the fight module), so it is not defined yet when this file loads.
const subscribeBufferedOdds = () => {
  if (oddsSubscribed) return;
  oddsSubscribed = true;
  store.subscribe((mutation) => {
    if (mutation.type.endsWith('fight/RESET_FIGHT')) {
      delete fightIdsByView[mutation.type.slice(0, -'/RESET_FIGHT'.length)];
      return;
    }
    if (!mutation.type.endsWith('fight/UPDATE_FIGHT')) return;
    const module = mutation.type.slice(0, -'/UPDATE_FIGHT'.length);
    const fight = store.getters[`${module}/fight`];
    const fightId = fight && fight.id;
    if (!fightId || fightIdsByView[module] === fightId) return;
    fightIdsByView[module] = fightId;
    const odds = latestOddsByFightId.get(fightId);
    // Only when the push is at least as new as what the view just loaded.
    if (odds && totalStaked(odds) >= totalStaked(fight)) store.commit(mutation.type, odds);
  });
};

// A fight update from any arena's connection: the root fight, each desktop
// cockfight pane and the Tài Xỉu tables all pick out what is theirs.
const handleFightUpdate = (type: string, data: any) => {
  subscribeBufferedOdds();
  if (type === 'fight_odds') bufferFightOdds(data);
  if (type === 'fight' || type === 'fight_v2' || type === 'fight_odds') {
    refreshIfUnknownArena(data);
    const { id: fightId, fightNumber } = store.getters['fight/fight'];
    const { id: arenaId } = store.getters['general/selectedArena'];
    const incomingArenaId = data?.match_day?.arena?.id;
    store.dispatch('taiXiu/updateFightFromSocket', data);

    // Desktop cockfight panes each follow their own arena; same routing rule
    // as the root fight below, applied to the pane's copy.
    getCockfightPaneNamespaces().forEach((namespace) => {
      const paneFight = store.getters[`${namespace}/fight/fight`];
      const paneArenaId = store.getters[`${namespace}/selectedArena`].id;
      if (isFightForArena(data, incomingArenaId, paneFight.id, paneArenaId, paneFight.fightNumber)) {
        store.dispatch(`${namespace}/fight/updateFight`, keysToCamel(data));
      }
    });

    if (!incomingArenaId) {
      // Arena ID is unknown — only safe to update if the fight ID matches exactly.
      // Skip arena status update to avoid attributing this message to the wrong arena.
      if (isFightForArena(data, incomingArenaId, fightId, arenaId, fightNumber)) {
        store.dispatch('fight/updateFight', keysToCamel(data));
      }
    } else {
      const status = data.result ? data.result : data.status;

      if (status && status !== arenasStatus[incomingArenaId]) {
        const arenaData = {
          id: incomingArenaId,
          fightStatus: status,
          isReverse: data?.match_day?.is_reverse,
        };

        store.dispatch('general/updateArenaStatus', arenaData);
        arenasStatus[incomingArenaId] = status;
      }

      if (isFightForArena(data, incomingArenaId, fightId, arenaId, fightNumber)) {
        store.dispatch('fight/updateFight', keysToCamel(data));
      }
    }
  }
};

const handleFightsMessage = (res: any) => {
  const { type, data } = res;
  handleFightUpdate(type, data);
  if (type === 'arena') {
    refreshArenaLists();
  }
  if (type === 'balance') {
    updateUserBalance(data.balance);
    // A balance push follows a settlement; keep an open Bet History current.
    store.dispatch('ticket/syncHistory');
  }
};

const wsFights = (token: string, oddsType: string) =>
  connectToFights(
    handleFightsMessage,
    () => {
      initMgConnection(token, oddsType);
      // Whatever was pushed while the socket was down is gone; reload the
      // current fight so a fight change in that gap is not missed.
      const arenaId = store.getters['general/selectedArena']?.id;
      if (arenaId) store.dispatch('fight/getCurrentFight', { arena_id: arenaId });
    },
    token,
    oddsType,
    GameType.fights
  );

/*
 * Desktop panes: one fights connection per arena on screen.
 *
 * The fights socket is subscribed per arena (`arena_id` in its URL), and the
 * main one follows the root `selectedArena` only. Two panes plus the quick-play
 * card can show up to three other arenas, each needing its own feed. These
 * connections carry fight updates only -- arena-list and balance pushes already
 * arrive on the main connection, and taking them from every socket would
 * refetch the arena list once per pane.
 */
const paneFightConnections: { [arenaId: string]: { key: string; connection: IWS | null } } = {};

const openPaneFightConnection = (token: string, oddsType: string, arenaId: string) => {
  const entry: { key: string; connection: IWS | null } = { key: `${token}:${oddsType}`, connection: null };
  paneFightConnections[arenaId] = entry;
  entry.connection = connectToFights(
    (res) => handleFightUpdate(res.type, res.data),
    () => {
      // Only the entry still registered for this arena may reconnect.
      if (paneFightConnections[arenaId] !== entry) return;
      openPaneFightConnection(token, oddsType, arenaId);
      // As for the root connection: reload the fight of every pane on this
      // arena, in case it changed while the socket was down.
      getCockfightPaneNamespaces()
        .filter((namespace) => store.getters[`${namespace}/selectedArena`].id === arenaId)
        .forEach((namespace) => store.dispatch(`${namespace}/fight/getCurrentFight`, { arena_id: arenaId }));
    },
    token,
    oddsType,
    GameType.fights,
    arenaId,
    `pane-fights:${arenaId}`
  );
};

const syncPaneFightConnections = (token: string, oddsType: string = 'eu', arenaIds: string[]) => {
  const key = `${token}:${oddsType}`;
  const wanted = token ? arenaIds.filter(Boolean) : [];
  Object.keys(paneFightConnections).forEach((arenaId) => {
    const entry = paneFightConnections[arenaId];
    if (wanted.includes(arenaId) && entry.key === key) return;
    delete paneFightConnections[arenaId];
    if (entry.connection && entry.connection.disconnect) entry.connection.disconnect();
  });
  wanted.forEach((arenaId) => {
    if (!paneFightConnections[arenaId]) openPaneFightConnection(token, oddsType, arenaId);
  });
};

const disconnectPaneFightConnections = () => syncPaneFightConnections('', 'eu', []);

const initTaiXiuConnection = (token: string, oddsType: string = 'eu', arenaId: string) => {
  disconnectTaiXiuConnection();
  if (!token || !arenaId) return;

  taiXiuConnection = connectToFights(
    (res) => {
      const { type, data } = res;

      if (type === 'fight' || type === 'fight_v2' || type === 'fight_odds') {
        refreshIfUnknownArena(data);
        store.dispatch('taiXiu/updateFightFromSocket', data);
      }
    },
    () => {
      initTaiXiuConnection(token, oddsType, arenaId);
    },
    token,
    oddsType,
    GameType.fights,
    arenaId,
    'tai-xiu-fights'
  );
};

const sendWsRequest = (
  token: string,
  gameType: GameType,
  payload: { matchDayId?: string; oddsType?: string; id?: string; lang?: string; platform?: string; drmType?: string }
) => {
  return sendMainRequest(
    (res) => {
      const { type, data, errorCode, errorMessage } = res;
      if (type === 'fight_result') {
        const fightResults = {
          fightsTotal: {
            bankerCount: data.bankerCount,
            drawCount: data.drawCount,
            playerCount: data.playerCount,
          },
          fights: getFightResultFromBinary(data.fights),
        };
        store.commit('fight/INSERT_CHART_FIGHTS', fightResults.fights);
        store.commit('fight/INSERT_CHART_FIGHTS_TOTAL', fightResults.fightsTotal);
      } else if (type === 'arena_stream') {
        if (data && errorCode === 'NO_ERROR') {
          store.commit('general/UPDATE_ARENA_STREAM_ERROR', '');
          store.commit('general/RECEIVE_ARENA_STREAM', data);
        } else if (errorCode === 'STREAM_MIN_BALANCE_ERROR') {
          store.commit('general/RECEIVE_ARENA_STREAM', '');
          store.commit('general/UPDATE_ARENA_STREAM_ERROR', errorMessage);
        } else {
          store.commit('general/DROP_ARENA_STREAM_RESPONSE');
        }
      } else if (type === 'arena_visibility_updated') {
        if (errorCode === 'NO_ERROR') {
          refreshArenaLists();
        } else {
          console.warn('Arena visibility update error:', errorMessage);
        }
      } else if (type === 'refresh_announcement') {
        store.dispatch('announcement/updateAnnouncement');
      }
    },
    token,
    gameType,
    payload
  );
};

const disconnectMgConnection = () => {
  if (mgConnection && !!mgConnection.disconnect) {
    mgConnection.disconnect();
  }
  mgConnection = {} as IWS;
};

const disconnectTaiXiuConnection = () => {
  if (taiXiuConnection && !!taiXiuConnection.disconnect) {
    taiXiuConnection.disconnect();
  }
  taiXiuConnection = null;
};

const disconnectMainConnection = () => {
  if (mainConnection && !!mainConnection.disconnect) {
    mainConnection.disconnect();
  }
  mainConnection = {} as IWS;
};

export {
  initMgConnection,
  disconnectMgConnection,
  initTaiXiuConnection,
  disconnectTaiXiuConnection,
  syncPaneFightConnections,
  disconnectPaneFightConnections,
  initMainConnection,
  reconnectMainConnection,
  sendWsRequest,
  disconnectMainConnection,
};
